import { ReceivementBatch } from '../../SwapOrReturnTypes'
import { FormHelperText, Typography } from '@mui/material'
import { formatDateReplaceHour } from 'src/utils/formatDate'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useCallback } from 'react'
import { actions } from '../../SwapOrReturnState'
import { useDispatch } from 'react-redux'
import Modal from '../../../../components/Modal'
import * as S from './BatchSelectionModal.styles'

interface BatchSelectionModalProps {
  show: boolean
  idModel: number
  batches: ReceivementBatch[]
  totalPending: number | undefined
  readOnly: boolean
  closeBatchModal: () => void
}

interface BatchFormValues {
  batches: ReceivementBatch[]
}

const BatchSelectionModal: React.FC<BatchSelectionModalProps> = ({
  show, idModel, batches, totalPending, readOnly, closeBatchModal,
}) => {
  const {
    control, handleSubmit, watch, setValue, setError, clearErrors, formState: { errors },
  } = useForm<BatchFormValues>({ defaultValues: { batches } })

  const { fields } = useFieldArray({
    control,
    name: 'batches',
  })

  const dispatch = useDispatch()

  const watchBatches = watch('batches')

  const totalSelectedQuantity = watchBatches.reduce((acc, item) => acc + (Number(item.inputQuantity) || 0), 0)

  const hasNotMetNecessary = totalSelectedQuantity !== totalPending

  const validateFieldValues = useCallback((batch: ReceivementBatch, inputQuantity: number | null, index: number) => {
    if (inputQuantity === null) return

    if (inputQuantity < 0) {
      setError(`batches.${index}.inputQuantity`, {
        message: 'Não pode ser negativo',
      })
      return
    }

    if (inputQuantity > batch.quantity) {
      setError(`batches.${index}.inputQuantity`, {
        message: 'Qtd. maior que o lote',
      })
      return
    }

    clearErrors(`batches.${index}.inputQuantity`)
  }, [clearErrors, setError])

  const closeBatchesModal = () => {
    closeBatchModal()
    clearErrors('batches')
    setValue('batches', batches)
  }

  const setBatches = (data: BatchFormValues) => {
    if (!totalPending) return
    dispatch(actions.setBatches({ batches: data.batches, idModel, inputQuantity: totalPending }))
    closeBatchModal()
  }

  const columns = ['Lote', 'Quantidade', 'Validade', 'Qtd. a retornar']

  return (
    <Modal
      show={show}
      title='Escolha as quantidades a retornar'
      centered
      size='lg'
      closeButtonAction={closeBatchesModal}
      testId='batch-selection-modal-test-id'
      body={(
        <S.Container onSubmit={handleSubmit(setBatches)}>
          <Typography fontStyle='italic'>Obs.: Informe as quantidades por lote, até que o total a retornar, seja igual ao total da pendência.</Typography>
          <Typography variant='h6' fontWeight={600}>Total da pendência: {totalPending}</Typography>
          <S.Row>
            {
              columns.map((column) => (
                <S.Column key={column}>
                  <Typography variant='h6'>{column}</Typography>
                </S.Column>
              ))
            }
          </S.Row>
          <S.ContainerRows>
            {
              fields.map((batch, index) => (
                <S.Row key={batch.idBatch}>
                  <S.Column>
                    <Typography>{batch.idBatch}</Typography>
                  </S.Column>
                  <S.Column>
                    <Typography>{batch.quantity}</Typography>
                  </S.Column>
                  <S.Column>
                    <Typography>{formatDateReplaceHour(new Date(batch.expirationDate))}</Typography>
                  </S.Column>
                  <S.Column>
                    <Controller
                      name={`batches.${index}.inputQuantity`}
                      control={control}
                      defaultValue={batch.inputQuantity}
                      render={({ field }) => (
                        <>
                          <S.CustomTextField
                            {...field}
                            id='input-quantity'
                            type='number'
                            autoComplete='off'
                            label=' '
                            size='small'
                            disabled={readOnly}
                            onChange={(e) => {
                              const { value } = e.target
                              let convertedInputQuantity: number | null = null
                              if (value === '') {
                                convertedInputQuantity = null
                              } else {
                                convertedInputQuantity = Number(value)
                              }
                              field.onChange({ ...e, target: { ...e.target, value: convertedInputQuantity } })
                              validateFieldValues(batch, convertedInputQuantity, index)
                            }}
                            error={!!errors.batches && !!errors.batches[index]}
                            InputLabelProps={{ shrink: false }}
                            inputProps={{
                              min: 0,
                              max: batch.quantity,
                              'data-testid': 'input-batch-quantity-test-id',
                            }}
                          />
                          {errors?.batches && errors.batches[index] && errors.batches[index].inputQuantity ? (
                            <FormHelperText error>
                              {errors?.batches[index]?.inputQuantity?.message ?? ''}
                            </FormHelperText>
                          ) : ''}
                        </>
                      )}
                    />
                  </S.Column>
                </S.Row>
              ))
            }
          </S.ContainerRows>
          <S.ContainerFooter>
            <S.TotalSelectedQuantity>
              <S.ContainerCounter>
                <Typography variant='h6' fontWeight={600}>{`Total a retornar: ${totalSelectedQuantity}`}</Typography>
              </S.ContainerCounter>
              {totalSelectedQuantity !== totalPending && totalSelectedQuantity > 0 && (
                <Typography variant='subtitle2' color='error'>(O total a retornar deve ser igual ao total da pendência.)</Typography>
              )}
            </S.TotalSelectedQuantity>
            <S.CustomButton
              type='submit'
              title='Salvar'
              bgColor='confirm'
              hoverColor='confirmHover'
              letterColor='white'
              disabled={hasNotMetNecessary || readOnly}
              data-testid='save-batches-button-test-id'
            />
          </S.ContainerFooter>
        </S.Container>
      )}
    />
  )
}

export default BatchSelectionModal
